import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { schoolClassSortOrder } from '../services/TimetableService';
import { storeService } from '../services/StoreService';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, AreaChart, Area
} from 'recharts';
import './AdminAnalyticsView.css';
import { scopeForAdmin } from '../utils/schoolScope';

function SuperAdminAnalyticsView({ adminData }) {
  const [saSchoolId, setSaSchoolId] = useState('');
  const [saSchools, setSaSchools] = useState([]);
  useEffect(() => {
    getDocs(collection(db, 'schools')).then(snap => setSaSchools(snap.docs.map(d => ({ id: d.id, ...d.data() })))).catch(() => {});
  }, []);
  adminData = useMemo(() => ({ ...(adminData || {}), schoolId: saSchoolId || adminData?.schoolId || null }), [adminData, saSchoolId]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [parents, setParents] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [events, setEvents] = useState([]);
  const [grades, setGrades] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [reportCards, setReportCards] = useState([]);
  const [teacherPreps, setTeacherPreps] = useState([]);
  const [storeAnalytics, setStoreAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAnalyticsData = useCallback(async () => {
    try {
      const schoolFilter = (col) => col;

      // Load classes
      const classesSnapshot = await getDocs(schoolFilter(collection(db, 'classes')));
      const classesData = scopeForAdmin(classesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })), adminData);
      classesData.sort(schoolClassSortOrder);
      setClasses(classesData);

      // Load students from each class
      const studentsData = [];
      for (const cls of classesData) {
        const snap = await getDocs(query(
          collection(db, 'classes', cls.id, 'students'),
          where('isActive', '==', true)
        ));
        snap.docs.forEach(doc => studentsData.push({ id: doc.id, classId: cls.id, ...doc.data() }));
      }
      setStudents(studentsData);

      // Load teachers
      const usersSnap = await getDocs(schoolFilter(collection(db, 'users')));
      setTeachers(scopeForAdmin(usersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })), adminData).filter(u => u.role === 'Teacher'));

      // Load parents
      const parentsSnap = await getDocs(schoolFilter(collection(db, 'parents')));
      setParents(scopeForAdmin(parentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })), adminData));

      // Load attendance (today)
      const today = new Date().toISOString().split('T')[0];
      const attendancePromises = classesData.map(classId =>
        getDocs(query(collection(db, 'classes', classId.id, 'attendanceRecords'), where('date', '==', today)))
      );
      const attendanceSnaps = await Promise.all(attendancePromises);
      const allAttendance = [];
      attendanceSnaps.forEach(snap => snap.docs.forEach(doc => allAttendance.push(doc.data())));
      setAttendanceRecords(allAttendance);

      // Load events
      const eventsSnap = await getDocs(schoolFilter(collection(db, 'events')));
      setEvents(eventsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // Load grades from each class's assignments
      const gradesData = [];
      for (const cls of classesData) {
        const assignmentsSnap = await getDocs(collection(db, 'classes', cls.id, 'assignments'));
        for (const assignmentDoc of assignmentsSnap.docs) {
          const gradesSnap = await getDocs(
            collection(db, 'classes', cls.id, 'assignments', assignmentDoc.id, 'grades')
          );
          gradesSnap.docs.forEach(doc => gradesData.push({ id: doc.id, classId: cls.id, ...doc.data() }));
        }
      }
      setGrades(gradesData);

      // Load grade change requests (approvals)
      const approvalsSnap = await getDocs(schoolFilter(collection(db, 'gradeChangeRequests')));
      setApprovals(approvalsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // Load report cards
      const reportCardsData = [];
      for (const cls of classesData) {
        const studentsSnap = await getDocs(collection(db, 'classes', cls.id, 'students'));
        for (const studentDoc of studentsSnap.docs) {
          const cardsSnap = await getDocs(
            collection(db, 'classes', cls.id, 'students', studentDoc.id, 'reportCards')
          );
          cardsSnap.docs.forEach(doc => reportCardsData.push({ id: doc.id, ...doc.data() }));
        }
      }
      setReportCards(reportCardsData);

      // Load teacher preps
      const prepsSnap = await getDocs(schoolFilter(collection(db, 'teacherPreps')));
      setTeacherPreps(prepsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // Load store analytics
      try {
        const storeData = await storeService.getStoreAnalytics();
        setStoreAnalytics(storeData);
      } catch (e) {
        console.error('Error loading store analytics:', e);
      }
    } catch (error) {
      console.error('Error loading analytics data:', error);
    } finally {
      setLoading(false);
    }
  }, [adminData]);

  useEffect(() => {
    loadAnalyticsData();
  }, [loadAnalyticsData]);

  // ===== Computed metrics =====

  const attendanceRate = attendanceRecords.length > 0
    ? (attendanceRecords.filter(r => r.status === 'present').length / attendanceRecords.length * 100).toFixed(1)
    : '0.0';

  const attendanceData = [
    { name: 'Present', value: attendanceRecords.filter(r => r.status === 'present').length, color: '#22c55e' },
    { name: 'Absent', value: attendanceRecords.filter(r => r.status === 'absent').length, color: '#ef4444' },
    { name: 'Late', value: attendanceRecords.filter(r => r.status === 'late').length, color: '#f59e0b' },
  ].filter(d => d.value > 0);

  const enrollmentByClass = classes.map(cls => ({
    name: cls.name,
    students: students.filter(s => s.classId === cls.id).length,
  })).filter(c => c.students > 0).sort((a, b) => b.students - a.students);

  // Grade distribution
  const gradeDistribution = [
    { name: 'A (90+)', value: 0, color: '#22c55e' },
    { name: 'B (80-89)', value: 0, color: '#22c55e' },
    { name: 'C (70-79)', value: 0, color: '#f59e0b' },
    { name: 'D (60-69)', value: 0, color: '#f97316' },
    { name: 'F (<60)', value: 0, color: '#ef4444' },
  ];
  grades.forEach(g => {
    if (g.score && g.maxScore) {
      const pct = (g.score / g.maxScore) * 100;
      if (pct >= 90) gradeDistribution[0].value++;
      else if (pct >= 80) gradeDistribution[1].value++;
      else if (pct >= 70) gradeDistribution[2].value++;
      else if (pct >= 60) gradeDistribution[3].value++;
      else gradeDistribution[4].value++;
    }
  });

  // Class average comparison
  const classAverages = classes.map(cls => {
    const classGrades = grades.filter(g => g.classId === cls.id);
    if (classGrades.length === 0) return null;
    const avg = classGrades.reduce((sum, g) => sum + (g.score / g.maxScore) * 100, 0) / classGrades.length;
    return { name: cls.name, average: parseFloat(avg.toFixed(1)) };
  }).filter(Boolean).sort((a, b) => b.average - a.average);

  // Teacher workload (classes per teacher)
  const teacherWorkload = teachers.map(t => {
    const teacherClasses = classes.filter(c => c.teacherId === t.id);
    return {
      name: `${t.firstName || ''} ${t.lastName || ''}`.trim() || t.username || 'Unknown',
      classes: teacherClasses.length,
      students: students.filter(s => teacherClasses.some(tc => tc.id === s.classId)).length,
    };
  }).filter(t => t.classes > 0).sort((a, b) => b.students - a.students);

  // Approvals status
  const approvalsData = [
    { name: 'Pending', value: approvals.filter(a => a.status === 'pending' || !a.status).length, color: '#f59e0b' },
    { name: 'Approved', value: approvals.filter(a => a.status === 'approved').length, color: '#22c55e' },
    { name: 'Rejected', value: approvals.filter(a => a.status === 'rejected').length, color: '#ef4444' },
  ].filter(d => d.value > 0);

  // Report cards by term
  const reportCardsByTerm = {};
  reportCards.forEach(rc => {
    const term = rc.term || 'Unspecified';
    reportCardsByTerm[term] = (reportCardsByTerm[term] || 0) + 1;
  });
  const reportCardsData = Object.entries(reportCardsByTerm).map(([term, count]) => ({ term, count }));

  // Teacher prep status
  const prepStatusData = [
    { name: 'Draft', value: teacherPreps.filter(p => p.status === 'draft' || !p.status).length, color: '#9ca3af' },
    { name: 'Submitted', value: teacherPreps.filter(p => p.status === 'submitted').length, color: '#22c55e' },
    { name: 'Approved', value: teacherPreps.filter(p => p.status === 'approved').length, color: '#22c55e' },
    { name: 'Revision', value: teacherPreps.filter(p => p.status === 'revision').length, color: '#f97316' },
  ].filter(d => d.value > 0);

  // Store revenue
  const storeRevenueData = storeAnalytics
    ? Object.entries(storeAnalytics.monthlyRevenue).map(([month, revenue]) => ({ month, revenue }))
    : [];

  // Parent connectivity
  const parentConnectivityData = [
    { name: 'Linked', value: parents.filter(p => p.linkedChildrenIds && p.linkedChildrenIds.length > 0).length, color: '#22c55e' },
    { name: 'Unlinked', value: parents.filter(p => !p.linkedChildrenIds || p.linkedChildrenIds.length === 0).length, color: '#ef4444' },
  ].filter(d => d.value > 0);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading analytics...</p>
      </div>
    );
  }

  return (
    <div className="analytics-dashboard">
      <h2>School Analytics</h2>
        <div className="sa-school-filter"><label>School:</label><select value={saSchoolId} onChange={(e) => setSaSchoolId(e.target.value)}><option value="">All Schools</option>{saSchools.map(s => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}</select></div>

      {/* Compact stat cards row — small, all in one row */}
      <div className="analytics-stats-row">
        <div className="mini-stat"><span className="mini-stat-icon">🏫</span><span className="mini-stat-value">{classes.length}</span><span className="mini-stat-label">Classes</span></div>
        <div className="mini-stat"><span className="mini-stat-icon">👥</span><span className="mini-stat-value">{students.length}</span><span className="mini-stat-label">Students</span></div>
        <div className="mini-stat"><span className="mini-stat-icon">👨‍🏫</span><span className="mini-stat-value">{teachers.length}</span><span className="mini-stat-label">Teachers</span></div>
        <div className="mini-stat"><span className="mini-stat-icon">👨‍👩‍👧</span><span className="mini-stat-value">{parents.length}</span><span className="mini-stat-label">Parents</span></div>
        <div className="mini-stat"><span className="mini-stat-icon">✅</span><span className="mini-stat-value">{attendanceRate}%</span><span className="mini-stat-label">Attendance</span></div>
        <div className="mini-stat"><span className="mini-stat-icon">📝</span><span className="mini-stat-value">{grades.length}</span><span className="mini-stat-label">Grades</span></div>
        <div className="mini-stat"><span className="mini-stat-icon">⏳</span><span className="mini-stat-value">{approvals.filter(a => a.status === 'pending' || !a.status).length}</span><span className="mini-stat-label">Pending Approvals</span></div>
        <div className="mini-stat"><span className="mini-stat-icon">📄</span><span className="mini-stat-value">{reportCards.length}</span><span className="mini-stat-label">Report Cards</span></div>
        <div className="mini-stat"><span className="mini-stat-icon">📋</span><span className="mini-stat-value">{teacherPreps.length}</span><span className="mini-stat-label">Teacher Preps</span></div>
        <div className="mini-stat"><span className="mini-stat-icon">📅</span><span className="mini-stat-value">{events.length}</span><span className="mini-stat-label">Events</span></div>
      </div>

      {/* Charts grid — smaller charts */}
      <div className="analytics-charts-grid">
        {/* Grade Distribution */}
        <div className="analytics-chart-card">
          <h3>Grade Distribution</h3>
          {gradeDistribution.some(d => d.value > 0) ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={gradeDistribution.filter(d => d.value > 0)} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70}>
                  {gradeDistribution.filter(d => d.value > 0).map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip />
                <Legend formatter={(value, entry) => `${value} (${entry.payload?.value ?? 0})`} />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart icon="📊" message="No grades recorded" />}
        </div>

        {/* Class Averages */}
        <div className="analytics-chart-card">
          <h3>Class Average Scores</h3>
          {classAverages.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={classAverages} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-30} textAnchor="end" height={60} interval={0} tick={{ fontSize: 10 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="average" fill="#22c55e" radius={[4, 4, 0, 0]} name="Average %" />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart icon="📈" message="No class averages" />}
        </div>

        {/* Attendance Breakdown */}
        <div className="analytics-chart-card">
          <h3>Attendance Today</h3>
          {attendanceData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={attendanceData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={70}>
                  {attendanceData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip />
                <Legend formatter={(value, entry) => `${value} (${entry.payload?.value ?? 0})`} />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart icon="📅" message="No attendance today" />}
        </div>

        {/* Enrollment by Class */}
        <div className="analytics-chart-card">
          <h3>Enrollment by Class</h3>
          {enrollmentByClass.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={enrollmentByClass} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-30} textAnchor="end" height={60} interval={0} tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="students" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Students" />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart icon="🏫" message="No enrollment data" />}
        </div>

        {/* Teacher Workload */}
        <div className="analytics-chart-card">
          <h3>Teacher Workload (Students)</h3>
          {teacherWorkload.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={teacherWorkload} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-30} textAnchor="end" height={60} interval={0} tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="students" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Students" />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart icon="👨‍🏫" message="No workload data" />}
        </div>

        {/* Approvals Status */}
        <div className="analytics-chart-card">
          <h3>Grade Change Approvals</h3>
          {approvalsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={approvalsData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70}>
                  {approvalsData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip />
                <Legend formatter={(value, entry) => `${value} (${entry.payload?.value ?? 0})`} />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart icon="✅" message="No grade change requests" />}
        </div>

        {/* Report Cards by Term */}
        <div className="analytics-chart-card">
          <h3>Report Cards by Term</h3>
          {reportCardsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={reportCardsData} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="term" tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#06b6d4" radius={[4, 4, 0, 0]} name="Report Cards" />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart icon="📄" message="No report cards" />}
        </div>

        {/* Teacher Prep Status */}
        <div className="analytics-chart-card">
          <h3>Teacher Prep Status</h3>
          {prepStatusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={prepStatusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70}>
                  {prepStatusData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip />
                <Legend formatter={(value, entry) => `${value} (${entry.payload?.value ?? 0})`} />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart icon="📋" message="No teacher preps" />}
        </div>

        {/* Parent Connectivity */}
        <div className="analytics-chart-card">
          <h3>Parent Connectivity</h3>
          {parentConnectivityData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={parentConnectivityData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={70}>
                  {parentConnectivityData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip />
                <Legend formatter={(value, entry) => `${value} (${entry.payload?.value ?? 0})`} />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart icon="👨‍👩‍👧" message="No parent data" />}
        </div>

        {/* Store Revenue Trend */}
        <div className="analytics-chart-card">
          <h3>Store Revenue Trend</h3>
          {storeRevenueData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={storeRevenueData} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={(v) => `EC$${v}`} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => `EC$${v.toFixed(2)}`} />
                <Area type="monotone" dataKey="revenue" stroke="#22c55e" fill="#22c55e" fillOpacity={0.3} strokeWidth={2} name="Revenue" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <EmptyChart icon="🛍️" message="No store revenue" />}
        </div>
      </div>

    </div>
  );
}

function EmptyChart({ icon, message }) {
  return (
    <div className="analytics-chart-empty">
      <span className="analytics-chart-empty-icon">{icon}</span>
      <span>{message}</span>
    </div>
  );
}

export default SuperAdminAnalyticsView;
